import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Zap, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money, num, Spinner, PageHeader, Empty, BRAND_DOT } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function QuickOrder() {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [items, setItems] = useState(null);
  const [cart, setCart] = useState({});
  const [channel, setChannel] = useState("Direct");
  const [orderRef, setOrderRef] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/brands").then((r) => { setBrands(r.data); setBrandId(r.data[0]?.id || ""); }); }, []);
  useEffect(() => { if (brandId) { setCart({}); api.get(`/menu-items?brand_id=${brandId}`).then((r) => setItems(r.data.filter((m) => m.active))); } }, [brandId]);

  const brand = brands.find((b) => b.id === brandId);
  const inc = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const dec = (id) => setCart((c) => ({ ...c, [id]: Math.max((c[id] || 0) - 1, 0) }));
  const lines = (items || []).filter((m) => cart[m.id] > 0);
  const total = lines.reduce((s, m) => s + m.selling_price * cart[m.id], 0);
  const count = lines.reduce((s, m) => s + cart[m.id], 0);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        brand_id: brandId, channel, order_ref: orderRef,
        items: lines.map((m) => ({ menu_item_id: m.id, qty: cart[m.id] })),
        status: "completed",
      };
      const { data } = await api.post("/orders", payload);
      toast.success(`Order ${data.order_ref} completed — ${money((data.consumption || {}).total_client_charge)} charged`);
      if (data.over_capacity) toast.warning("Order placed OVER CAPACITY for this brand");
      if (data.off_hours) toast.warning("Order placed outside operating hours");
      setCart({}); setOrderRef("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Quick Order Entry" subtitle="Tap to build · one press completes the order and deducts stock">
        <Button variant="outline" onClick={() => navigate("/orders")} className="gap-1.5"><Clock className="h-4 w-4" /> All Orders</Button>
      </PageHeader>

      <div className="grid lg:grid-cols-3 gap-3 mb-6">
        {brands.map((b) => (
          <button key={b.id} onClick={() => setBrandId(b.id)} data-testid={`quick-brand-${b.id}`}
            className={`flex items-center gap-2.5 p-4 rounded-xl border text-left transition-colors ${brandId === b.id ? "border-primary bg-primary/10" : "border-border hover:border-primary/40 bg-card"}`}>
            <span className={`h-3 w-3 rounded-full ${BRAND_DOT[b.color] || "bg-slate-400"}`} />
            <div className="min-w-0">
              <p className="font-semibold text-white truncate">{b.name}</p>
              <p className="text-[11px] text-muted-foreground font-mono">{b.op_start}–{b.op_end}</p>
            </div>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {!items ? <Spinner /> : items.length === 0 ? <Empty icon={Zap} title="No active menu items for this brand" /> : (
            <div className="grid sm:grid-cols-2 gap-3">
              {items.map((m) => (
                <div key={m.id} className="card-surface p-4 flex items-center justify-between gap-3" data-testid={`quick-item-${m.id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-white truncate">{m.name}</p>
                    <p className="text-xs font-mono text-muted-foreground">{money(m.selling_price)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => dec(m.id)} data-testid={`dec-${m.id}`}><Minus className="h-4 w-4" /></Button>
                    <span className="w-8 text-center font-mono font-bold text-white text-lg" data-testid={`qty-${m.id}`}>{cart[m.id] || 0}</span>
                    <Button size="icon" className="h-9 w-9 bg-primary text-primary-foreground hover:bg-amber-400" onClick={() => inc(m.id)} data-testid={`inc-${m.id}`}><Plus className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:sticky lg:top-24 h-fit card-surface p-5">
          <h3 className="font-display font-semibold text-white mb-3">Order Summary</h3>
          <div className="space-y-2 mb-3">
            <Input placeholder="Order reference (optional)" value={orderRef} onChange={(e) => setOrderRef(e.target.value)} data-testid="quick-order-ref" />
            <div className="flex gap-2">
              {["Direct", "Phone", "Walk-in"].map((c) => (
                <button key={c} onClick={() => setChannel(c)} className={`flex-1 text-xs py-2 rounded-lg border ${channel === c ? "border-primary text-amber-300 bg-primary/10" : "border-border text-muted-foreground"}`}>{c}</button>
              ))}
            </div>
          </div>
          {lines.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Add items to build the order.</p>
          ) : (
            <div className="space-y-1.5 mb-4">
              {lines.map((m) => (
                <div key={m.id} className="flex justify-between text-sm">
                  <span className="text-slate-200">{m.name} × {cart[m.id]}</span>
                  <span className="font-mono text-slate-300">{money(m.selling_price * cart[m.id])}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-between items-center pt-3 border-t border-border mb-4">
            <span className="text-sm text-muted-foreground">{count} item{count !== 1 ? "s" : ""}</span>
            <span className="font-mono font-bold text-amber-300 text-xl" data-testid="quick-total">{money(total)}</span>
          </div>
          {brand && (() => {
            const now = new Date();
            const hhmm = now.toTimeString().slice(0, 5);
            const off = !(brand.op_start <= hhmm && hhmm <= brand.op_end);
            return off ? <p className="flex items-center gap-1.5 text-xs text-amber-400 mb-3"><AlertTriangle className="h-3.5 w-3.5" /> Outside operating hours ({brand.op_start}–{brand.op_end})</p> : null;
          })()}
          <Button onClick={submit} disabled={busy || count === 0} data-testid="quick-order-submit"
            className="w-full h-12 text-base bg-primary text-primary-foreground hover:bg-amber-400 font-bold gap-2">
            <Zap className="h-5 w-5" /> Complete Order
          </Button>
        </div>
      </div>
    </div>
  );
}
